/**
 * App state.
 *
 * `GET /api/lms/me` is the first request every screen waits on, and its answer
 * is one of seven statuses rather than a value and an error. `Me` is `MeWire`
 * itself — the contract in `worker/wire.ts` — not a second declaration of it:
 * the placeholder this file shipped with had `id`, `name` and `email` at the
 * top level, and the route nests them under `user`.
 *
 * `unreachable` is kept separate from `signedOut`, the distinction Cloud
 * learned the hard way: collapsing a 500 or a dropped connection into "signed
 * out" bounces a signed-in user to the sign-in page for a network blip, which
 * then looks like the session broke.
 *
 * A 403 here is always `second_factor_required`. The access matrix's refusal
 * (`app_not_allowed`) applies to mutations — a read is never refused — so it
 * cannot arrive on this route, and mapping every 403 onto the second-factor
 * screen is safe rather than lucky.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore
} from 'react';
import type React from 'react';
import {
  applyBranding,
  applyCachedBranding,
  getBranding,
  setProduct,
  subscribeBranding
} from '@shared/apply-branding';
import type { Branding } from '@shared/branding';
import { startPresence, stopPresence } from '@shared/presence-client';
import type { MeWire } from '@wire';
import { ApiError, api } from './api';

export type AppStatus =
  | 'loading'
  | 'signedOut'
  | 'secondFactor'
  /** A member with no organisation — Learn is Enterprise-only. See the plan's
   *  API routes section: `GET /me` answers 200 with `organisation: null`
   *  rather than 404, so this is a real status and not an error path. */
  | 'outside'
  /** The deployment is incomplete — a missing binding, an unmigrated database.
   *  Separated from `unreachable` because the two need completely different
   *  words: one is "wait a moment", the other is "finish the deploy". */
  | 'notReady'
  | 'unreachable'
  | 'ready';

/** Whatever the server could tell us about why it is not ready. */
export interface NotReady {
  message: string;
  missingTables?: string[];
  hint?: string;
}

/** The route's own type. Re-exported so a page imports it from here, beside
 *  `useApp`, rather than reaching into the wire contract for one shape. */
export type Me = MeWire;

interface AppValue {
  status: AppStatus;
  me: Me | null;
  notReady: NotReady | null;
  refresh: () => Promise<void>;
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
}

const Ctx = createContext<AppValue | null>(null);

export function useApp(): AppValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

const THEME_KEY = 'mauvely-lms-theme';

/**
 * Theme is the one thing worth persisting locally.
 *
 * It is a display preference and reveals nothing, which is why this app uses
 * localStorage for it without the hesitation the encrypted apps have about the
 * same API. Copied from Cloud's `state.tsx`.
 */
function initialTheme(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // Private mode, or storage blocked. The default is fine.
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * The organisation's branding from the last load, before anything renders.
 *
 * Module scope on purpose: a branded site otherwise paints Mauvely violet under
 * the word `learn.`, asks who the viewer is, and repaints. The server's answer
 * replaces this the moment it arrives, so a stale value survives exactly one
 * paint.
 */
setProduct({ name: 'Mauvely LMS', short: 'Learn' });
applyCachedBranding();

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AppStatus>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [notReady, setNotReady] = useState<NotReady | null>(null);
  const [theme, setThemeState] = useState<'light' | 'dark'>(initialTheme);

  // The design system reads `data-theme` off the document, so the toggle is one
  // attribute rather than a second set of styles.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Not worth surfacing — the theme still applies for this session.
    }
  }, [theme]);

  const refresh = useCallback(async () => {
    try {
      const next = await api.me();
      setMe(next);
      // Here rather than in an effect: this is where the server's answer
      // arrives, and an effect would repaint a frame later.
      applyBranding(next.branding);
      setNotReady(null);
      setStatus(next.organisation ? 'ready' : 'outside');
      return;
    } catch (err) {
      if (!(err instanceof ApiError) || err.code === 'network') {
        // No response at all. The only case that is really a network problem.
        setStatus('unreachable');
        return;
      }
      if (err.status === 401) {
        setMe(null);
        // A signed-out viewer is nobody's member. Keeping the colour would
        // leave a customer's branding on a screen that belongs to Mauvely, and
        // on a shared machine it says which company the last person worked for.
        applyBranding(null);
        setStatus('signedOut');
        return;
      }
      if (err.status === 403) {
        // Authenticated but has not cleared the second factor. Its own state,
        // because sending this to /signin mints another session in the same
        // condition and the user loops forever.
        setMe(null);
        applyBranding(null);
        setStatus('secondFactor');
        return;
      }
      setNotReady(await diagnose(err));
      setStatus('notReady');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * The presence socket, for as long as somebody is signed in.
   *
   * Keyed on the **id**, not on `me`: every `/me` refresh hands back a fresh
   * object, and an effect keyed on that would tear the socket down and build it
   * again on each one.
   *
   * `stopPresence` in the cleanup and on the way out of a session, because a
   * socket that outlived the session would keep saying somebody was here about
   * whoever signs in next at this browser. `path` is what worker/presence.ts
   * forwards; nothing else in the app has an account to be present for.
   */
  // Not for somebody on a magic link: presence is a Mauvely account's, and
  // they have none.
  const userId = me && !me.external ? me.user.id : null;
  useEffect(() => {
    if (!userId) {
      stopPresence();
      return;
    }
    startPresence({ app: 'lms', path: '/api/lms/presence' });
    return () => stopPresence();
  }, [userId]);

  return (
    <Ctx.Provider value={{ status, me, notReady, refresh, theme, setTheme: setThemeState }}>
      {children}
    </Ctx.Provider>
  );
}

/**
 * Turn a failed response into something a person can act on.
 *
 * The health endpoint already knows how to say "these tables are missing", so
 * ask it rather than inventing a second vocabulary.
 */
async function diagnose(err: ApiError): Promise<NotReady> {
  // The 503 from /me already carries the health payload inline, on the same
  // error object `request` unwrapped.
  const details = err.details as { missingTables?: string[]; hint?: string };
  if (Array.isArray(details.missingTables) || typeof details.hint === 'string') {
    return { message: err.message, missingTables: details.missingTables, hint: details.hint };
  }

  if (err.status === 404) {
    return {
      message:
        'The Worker is serving the app but not its API. The deployed build is ' +
        'older than this page, or wrangler.jsonc is missing run_worker_first for /*.',
      hint: 'Redeploy with `npm run deploy`.'
    };
  }

  try {
    const health = await api.health();
    return {
      message:
        health.database === 'unreachable'
          ? 'The D1 binding is not answering. Check `database_id` in wrangler.jsonc.'
          : 'Learn is deployed but not finished setting up.',
      missingTables: health.missingTables
    };
  } catch {
    return { message: `The API answered ${err.status} and the health check did not answer.` };
  }
}

/**
 * The organisation's branding, or null for Mauvely.
 *
 * Deliberately **not** on `AppContext`. `Wordmark` is the one place that reads
 * this, and a prop threaded down to it would be a second way to ask the same
 * question `applyBranding`'s module-level store already answers.
 */
export function useBranding(): Branding | null {
  return useSyncExternalStore(subscribeBranding, getBranding, getBranding);
}
